import React, { useEffect, useState, useRef } from "react";
import MainLayout from "../../components/layouts/MainLayout";
import {
  EditFilled,
  DeleteFilled,
  PlusOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import { FraisAbonnementApi } from "../../api/fraisAbonnementApi";
import { Auth } from "../../utils/Auth";
import { message, Modal, Form, Input, InputNumber, Tooltip, Spin, Select } from "antd";
import Swal from "sweetalert2";
import Money from "../../components/Money";

const { confirm } = Modal;

const ListFraisAbonnementsView = () => {
  const token = Auth.getSessionToken();
//   const abortControllerRef = new AbortController();
    const abortController = new AbortController()
  const [frais, setFrais] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [current, setCurrent] = useState(null);

  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await FraisAbonnementApi.getFraisAbonnement(abortController.signal);
      console.log("Frais d'abonnement:", res.data);
      if (res && res.data) setFrais(res.data);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors du chargement des frais d'abonnement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAdd = () => {
    form.resetFields();
    setIsAddOpen(true);
  };

  const handleAdd = async (values) => {
    try {
      setLoading(true);
      const res = await FraisAbonnementApi.createFraisAbonnement(JSON.stringify(values), abortController.signal);
      if (res && res.success) {
        message.success("Frais ajouté");
        setIsAddOpen(false);
        fetchData();
      } else {
        message.error(res?.message || "Erreur lors de la création");
      }
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (item) => {
    setCurrent(item);
    form.setFieldsValue({ type: item.type, amount: item.amount, description : item.description, status: item.status });
    setIsEditOpen(true);
  };

  const handleEdit = async (values) => {
    if (!current) return;
    try {
      setLoading(true);
      const res = await FraisAbonnementApi.updateFraisAbonnement(current.id, JSON.stringify(values), abortController.signal);
      if (res && res.success) {
        message.success("Frais mis à jour");
        setIsEditOpen(false);
        setCurrent(null);
        fetchData();
      } else {
        message.error(res?.message || "Erreur lors de la mise à jour");
      }
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la mise à jour");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = (item) => {
    confirm({
      title: "Confirmer la suppression",
      content: `Voulez-vous supprimer le frais "${item.type}" de ${item.amount} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          setLoading(true);
          const res = await FraisAbonnementApi.deleteFraisAbonnement(item.id, abortController.signal);
          if (res && res.success) {
            message.success("Frais supprimé");
            fetchData();
          } else {
            message.error(res?.message || "Erreur lors de la suppression");
          }
        } catch (err) {
          console.error(err);
          message.error("Erreur lors de la suppression");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleActivateConfirm = (item) => {
    confirm({
      title: item.status === 'active' ? "Désactiver le frais" : "Activer le frais",
      content: `Voulez-vous ${item.status === 'active' ? "désactiver" : "activer"} le frais "${item.type}" de ${item.amount} ?`,
      okText: item.status === 'active' ? "Désactiver" : "Activer",
      okType: item.status === 'inactive' ? "danger" : "primary",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          setLoading(true);
          const res = await FraisAbonnementApi.activateFraisAbonnement(item.id, abortController.signal);
          if (res && res.success) {
            message.success(item.status === 'active' ? "Frais désactivé" : "Frais activé");
            fetchData();
          } else {
            message.error(res?.message || "Erreur lors de la modification");
          }
        } catch (err) {
          console.error(err);
          message.error("Erreur lors de la modification");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <MainLayout>
      <div className="container mx-auto p-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl font-bold">Gestion des frais d'abonnement</h1>
          <button onClick={openAdd} className="px-4 py-2 text-white bg-africasa-secondary rounded hover:bg-africasa-primary flex items-center">
            <PlusOutlined className="mr-2" /> Ajouter un frais
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <Spin />
          </div>
        ) : (
          <table className="min-w-full bg-white border border-gray-300">
            <thead>
              <tr className="bg-gray-200">
                <th className="py-2 px-4 border-b text-left">#</th>
                <th className="py-2 px-4 border-b text-left">Type d'abonnement</th>
                <th className="py-2 px-4 border-b text-left">Montant</th>
                <th className="py-2 px-4 border-b text-left">Statut</th>
                <th className="py-2 px-4 border-b text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {frais.map((f, idx) => (
                <tr key={f.id}>
                  <td className="py-2 px-4 border-b">{idx + 1}</td>
                  <td className="py-2 px-4 border-b">{f.type}</td>
                  <td className="py-2 px-4 border-b"><Money amount={f.amount} /></td>
                  <td className="py-2 px-4 border-b">
                    {f.status === 'active' ? (
                      <span className="text-green-200 bg-green-700 p-2 text-sm rounded-md">Actif</span>
                    ) : (
                      <span className="text-red-200 bg-red-700 p-2 text-sm rounded-md">Désactivé</span>
                    )}
                  </td>
                  <td className="py-2 px-4 border-b">
                    <Tooltip title="Modifier">
                      <EditFilled className="text-blue-600 mr-2 cursor-pointer" onClick={() => openEdit(f)} />
                    </Tooltip>
                    <Tooltip title={f.status === 'active' ? "Désactiver" : "Activer"}>
                      {f.status === 'active' ? (
                        <CloseCircleFilled className="text-red-700 mr-2 cursor-pointer" onClick={() => handleActivateConfirm(f)} />
                      ) : (
                        <CheckCircleFilled className="text-green-700 mr-2 cursor-pointer" onClick={() => handleActivateConfirm(f)} />
                      )}
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <DeleteFilled className="text-red-600 cursor-pointer" onClick={() => handleDeleteConfirm(f)} />
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Add Modal */}
        <Modal
          title="Ajouter un frais d'abonnement"
          open={isAddOpen}
          onCancel={() => setIsAddOpen(false)}
          onOk={() => form.submit()}
          okText="Ajouter"
        >
          <Form form={form} layout="vertical" onFinish={handleAdd}>
            <Form.Item name="type" label="Type d'abonnement" rules={[{ required: true }]}> 
              <Select placeholder="Sélectionnez le type d'abonnement">
                <Select.Option value="mensuel">Mensuel</Select.Option>
                <Select.Option value="annuel">Annuel</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="amount" label="Montant" rules={[{ required: true }]}> 
              <InputNumber className="w-full" min={0} />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} />
            </Form.Item>
              <Form.Item name="status" label="Statut" rules={[{ required: false }]}>
                <Select>
                  <Select.Option value="inactive">Inactif</Select.Option>
                  <Select.Option value="active">Actif</Select.Option>
                </Select>
              </Form.Item>
          </Form>
        </Modal>

        {/* Edit Modal */}
        <Modal
          title="Modifier un frais d'abonnement"
          open={isEditOpen}
          onCancel={() => { setIsEditOpen(false); setCurrent(null); }}
          onOk={() => form.submit()}
          okText="Enregistrer"
        >
          <Form form={form} layout="vertical" onFinish={handleEdit}>
            <Form.Item name="type" label="Type d'abonnement" rules={[{ required: true }]}> 
              {/* <Input /> */}
               <Select>
                <Select.Option value="mensuel">Mensuel</Select.Option>
                <Select.Option value="annuel">Annuel</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item name="amount" label="Montant" rules={[{ required: true }]}> 
              <InputNumber className="w-full" min={0} />
            </Form.Item>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="status" label="Statut" rules={[{ required: false }]}> 
              <Select>
                <Select.Option value="active">Actif</Select.Option>
                <Select.Option value="inactive">Inactif</Select.Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ListFraisAbonnementsView;
